// Future Booking — demand outlook plus deposit position for every
// upcoming booked appointment. Columns are strictly plain-text (no
// links, no drill-through) per operator brief.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Search } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function FutureBookingReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("this_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = { ...range };
      if (search.trim()) params.q = search.trim();
      const r = await staffApi.get(`/reports/branch/${bid}/future-bookings`, { params });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range, search]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers on Deposit + Nominal.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const num = { numeric: true, format: money, align: "right" };
    return {
      filename: "future-bookings",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Date",              key: "date" },
        { header: "Time",              key: "time" },
        { header: "Client Name",       key: "client_name" },
        { header: "Treatment/Package", key: "treatment" },
        { header: "Deposit Received",  key: "deposit_received", ...num },
        { header: "Nominal Total",     key: "order_total",      ...num },
      ],
      rows: rows.map((r) => ({
        date: fmtDate(r.date),
        time: r.time || "—",
        client_name: r.client_name,
        treatment: r.package_name || r.service_name || "—",
        deposit_received: Number(r.deposit_received) || 0,
        order_total: Number(r.order_total) || 0,
      })),
      footerRow: [
        "Totals", "", `${rows.length} booking${rows.length === 1 ? "" : "s"}`, "",
        Number(data.total_deposit) || 0, Number(data.total_order_value) || 0,
      ],
    };
  }, [data, rows, branch]);

  return (
    <ReportShell
      title="Future Booking"
      subtitle="Every upcoming booked appointment with the deposit already collected against the linked order."
      branchName={branch?.name}
      tab="customers"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
          forwardOnly
        />

        <Card className="rounded-lg">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Treatment / Package</span>
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. Botox, Laser, Wellness Package…"
                className="pl-8"
                data-testid="future-booking-search"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg overflow-hidden" data-testid="future-booking-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Future Booking</h2>
              </div>
              {data && (
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Bookings</div>
                    <div className="text-xl font-semibold" data-testid="future-booking-count">{data.count}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total deposit</div>
                    <div className="text-xl font-semibold" data-testid="future-booking-total-deposit">{formatBranchMoney(branch, data.total_deposit)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total booking value</div>
                    <div className="text-xl font-semibold" data-testid="future-booking-total-order">{formatBranchMoney(branch, data.total_order_value)}</div>
                  </div>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider w-32">Date</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider w-20">Time</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Client Name</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Treatment / Package</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right w-40">Deposit Received</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right w-44">Nominal Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8" data-testid="future-booking-empty">
                      {loading ? "Loading…" : "No upcoming bookings in this range."}
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.appointment_id} className="hover:bg-secondary/20" data-testid={`future-booking-row-${r.appointment_id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{r.time || "—"}</TableCell>
                    <TableCell className="text-sm">{r.client_name}</TableCell>
                    <TableCell className="text-sm">
                      {r.package_name ? (
                        <span>{r.package_name}{r.service_name ? <span className="text-muted-foreground text-xs"> · {r.service_name}</span> : null}</span>
                      ) : (r.service_name || <span className="text-muted-foreground">—</span>)}
                    </TableCell>
                    <TableCell className="text-right text-sm" data-testid={`future-booking-deposit-${r.appointment_id}`}>
                      {formatBranchMoney(branch, r.deposit_received)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium" data-testid={`future-booking-nominal-${r.appointment_id}`}>
                      {formatBranchMoney(branch, r.order_total)}
                    </TableCell>
                  </TableRow>
                ))}
                {data && rows.length > 0 && (
                  <TableRow className="bg-muted font-semibold" data-testid="future-booking-totals-row">
                    <TableCell colSpan={4}>Totals · {data.count} booking{data.count === 1 ? "" : "s"}</TableCell>
                    <TableCell className="text-right">{formatBranchMoney(branch, data.total_deposit)}</TableCell>
                    <TableCell className="text-right">{formatBranchMoney(branch, data.total_order_value)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
